import React, { useEffect, useId, useMemo, useState } from "react";
import { Typography, Box, darken, lighten } from "@mui/material";
import { TouchApp } from "@mui/icons-material";

import { INVITATION_OCCASION, Invitations } from "../../../../../store/services/invitations/dto/invitations.dto";
import { CurtainEntranceWrapper } from "./styles";
import { getCurtainParts } from "./curtainGeometry";
import { LANG_AR, LANG_EN, RTL } from "../../../../../Components/Templates/WeddingTemplates/constants";
import langEN from "../../../../../Components/Templates/WeddingTemplates/lang.EN.json";
import langAR from "../../../../../Components/Templates/WeddingTemplates/lang.AR.json";
import { TemplateColorObject } from "../../../../../InvitationColorMatrix";

const OPEN_DURATION = 2200;

const CurtainEntrance = ({
	isOpened,
	setIsOpened,
	selectedInvitation,
	colorMatrix,
	preview
}: {
	isOpened: boolean;
	setIsOpened: React.Dispatch<React.SetStateAction<boolean>>;
	selectedInvitation: Invitations;
	colorMatrix: TemplateColorObject;
	preview?: boolean;
}) => {
	const lang = selectedInvitation?.language === LANG_EN ? langEN : langAR;

	const gradientId = `curtain${useId().replace(/:/g, "")}`;
	const base = colorMatrix.entrance.envelope_background;
	const [progress, setProgress] = useState(isOpened ? 1 : 0);

	useEffect(() => {
		const from = progress;
		const to = isOpened ? 1 : 0;
		if (from === to) return;
		const start = performance.now();
		let frame = requestAnimationFrame(function tick(now) {
			const t = Math.min(1, (now - start) / OPEN_DURATION);
			setProgress(from + (to - from) * t);
			if (t < 1) frame = requestAnimationFrame(tick);
		});
		return () => cancelAnimationFrame(frame);
	}, [isOpened]);

	const parts = useMemo(() => getCurtainParts(progress), [progress]);

	const invitationType = useMemo(() => {
		if (!selectedInvitation) return;
		switch (selectedInvitation?.occasion) {
			case INVITATION_OCCASION.KATB_KTAB: {
				return lang.the_katb_ktab_of;
			}
			case INVITATION_OCCASION.ENGAGEMENT: {
				return lang.the_engagement_of;
			}
			default: {
				return lang.the_wedding_of;
			}
		}
	}, [selectedInvitation]);

	return (
		<CurtainEntranceWrapper
			className={`${isOpened ? "hide opened" : "show"} ${preview ? "preview" : ""}`}
			onClick={() => setIsOpened(true)}
			colormatrix={colorMatrix}
		>
			<div className={"stage"}>
				<div className={`couples_banner ${selectedInvitation?.language === LANG_AR && RTL}`}>
					<Typography className={"wedding_of"}>{invitationType}</Typography>
					<Box display={"flex"} justifyContent={"center"} gap={1}>
						<Typography className={"couples groom"}>{selectedInvitation?.groom_name}</Typography>
						<Typography className={"couples and"}> {lang.and} </Typography>
						<Typography className={"couples bride"}> {selectedInvitation?.bride_name} </Typography>
					</Box>
				</div>
			</div>

			<svg className={"curtains"} viewBox={"0 0 100 100"} preserveAspectRatio={"none"}>
				<defs>
					{[0, 1].map(variant => (
						<linearGradient key={variant} id={`${gradientId}_${variant}`} x1={"0"} y1={"0"} x2={"1"} y2={"0"}>
							<stop offset={"0%"} stopColor={darken(base, 0.5)} />
							<stop offset={variant ? "30%" : "22%"} stopColor={darken(base, 0.12)} />
							<stop offset={variant ? "48%" : "58%"} stopColor={lighten(base, 0.28)} />
							<stop offset={variant ? "75%" : "82%"} stopColor={darken(base, 0.2)} />
							<stop offset={"100%"} stopColor={darken(base, 0.55)} />
						</linearGradient>
					))}
					<linearGradient id={`${gradientId}_shade`} x1={"0"} y1={"0"} x2={"0"} y2={"1"}>
						<stop offset={"0%"} stopColor={"rgba(0,0,0,0.5)"} />
						<stop offset={"22%"} stopColor={"rgba(0,0,0,0)"} />
						<stop offset={"80%"} stopColor={"rgba(0,0,0,0)"} />
						<stop offset={"100%"} stopColor={"rgba(0,0,0,0.3)"} />
					</linearGradient>
				</defs>
				{parts.folds.map((fold, index) => (
					<g key={index}>
						<path d={fold.d} fill={`url(#${gradientId}_${fold.variant})`} />
						<path d={fold.d} fill={`url(#${gradientId}_shade)`} />
					</g>
				))}
				<g opacity={parts.tiebackOpacity}>
					{parts.tiebacks.map((d, index) => (
						<path
							key={index}
							d={d}
							fill={"none"}
							stroke={lighten(colorMatrix.main, 0.1)}
							strokeWidth={9}
							strokeLinecap={"round"}
							vectorEffect={"non-scaling-stroke"}
						/>
					))}
				</g>
			</svg>
			<div className={"curtain_rod"} />
			<svg className={"valance"} viewBox={"0 0 100 20"} preserveAspectRatio={"none"}>
				<defs>
					<linearGradient id={"valance_gradient"} x1={"0"} y1={"0"} x2={"0"} y2={"1"}>
						<stop offset={"0%"} stopColor={"rgba(255,255,255,0.35)"} />
						<stop offset={"100%"} stopColor={"rgba(0,0,0,0.35)"} />
					</linearGradient>
				</defs>
				<path
					className={"valance_shape"}
					d={"M0 0H100V8Q91.7 22 83.3 8Q75 22 66.7 8Q58.3 22 50 8Q41.7 22 33.3 8Q25 22 16.7 8Q8.3 22 0 8Z"}
				/>
				<path
					d={"M0 0H100V8Q91.7 22 83.3 8Q75 22 66.7 8Q58.3 22 50 8Q41.7 22 33.3 8Q25 22 16.7 8Q8.3 22 0 8Z"}
					fill={"url(#valance_gradient)"}
				/>
			</svg>

			<Box className={"tab_continue_container"} display={"flex"} flexDirection={"column"}>
				<Box
					className={`tab_continue ${isOpened ? "animate__animated animate__fadeOut" : "animate__animated animate__headShake animate__delay-2s animate__infinite"}`}
				>
					<TouchApp />
				</Box>
				<Typography className={`tab_continue_text ${isOpened ? "animate__animated animate__fadeOut" : ""}`}>
					{lang.TAP_TO_OPEN_CURTAIN}
				</Typography>
			</Box>
		</CurtainEntranceWrapper>
	);
};

export default CurtainEntrance;
